import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import ApproachFlow from "@/components/ApproachFlow";
import { Reveal } from "@/components/Reveal";
import { useTitle } from "@/hooks/use-title";
import { METHODOLOGY, EXPERIENCE_AREAS, LINKEDIN_URL, EXPERIENCE_URL } from "@/data/content";

export default function About() {
  useTitle("About — Operating Reality");
  return (
    <main className="pt-16 lg:pt-[72px]" data-testid="about-page">
      <section className="relative overflow-hidden border-b border-line/50">
        <div className="bg-grid absolute inset-0 opacity-30" />
        <div className="wrap relative grid gap-12 py-24 lg:grid-cols-12 lg:py-36">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="kicker">About</p>
              <h1 className="mt-6 text-balance text-4xl font-semibold leading-[1.1] tracking-tight text-slate-50 sm:text-5xl">
                I'm interested in what happens between the process and the technology.
              </h1>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="mt-10 max-w-xl space-y-4 text-base leading-relaxed text-slate-400">
                <p>Organisations rarely experience transformation problems in neat categories.</p>
                <p>A process problem can become a technology problem.</p>
                <p>A governance gap can become an operational risk.</p>
                <p>A knowledge dependency can become a resilience problem.</p>
                <p className="text-slate-200">My work sits in the space between those things.</p>
              </div>
            </Reveal>
          </div>
          <div className="flex items-start lg:col-span-5 lg:justify-end">
            <Reveal delay={0.18}>
              <img
                src="/assets/or-mark.png"
                alt="Operating Reality monogram"
                className="h-28 w-28 rounded-full border border-line/60 object-cover lg:h-36 lg:w-36"
                data-testid="about-brand-mark"
              />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="wrap grid gap-12 py-24 lg:grid-cols-12 lg:py-32">
        <div className="lg:col-span-4">
          <Reveal>
            <p className="kicker">The person</p>
            <h2 className="mt-5 text-3xl font-medium tracking-tight text-slate-100">Anna Krat</h2>
            <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
              Technical Product & Digital Transformation Professional
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-8">
          <Reveal delay={0.1}>
            <div className="max-w-2xl space-y-5 text-base leading-relaxed text-slate-400">
              <p>
                I work with organisations to understand how they actually operate, identify what
                is creating friction or risk, and turn that understanding into practical change.
              </p>
              <p>
                I combine strategic thinking with hands-on implementation — from process
                discovery and governance through to systems, digital workplace, automation and AI.
              </p>
              <p>
                The principle is simple: start with the problem, not the technology, and look at
                the organisation as a system rather than from a single departmental perspective.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-line/50 bg-charcoal/20">
        <div className="wrap py-24 lg:py-32">
          <SectionHeader
            kicker="How I work"
            title="Start with what's actually happening."
          />
          <Reveal delay={0.08}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400">
              Before recommending a technology, changing a process or designing a
              transformation programme, I first look at how work actually happens — where it
              gets stuck, who owns it, what people depend on, and where the real risks are.
            </p>
          </Reveal>
          <div className="mt-16 lg:mt-20">
            <ApproachFlow steps={METHODOLOGY} />
          </div>
        </div>
      </section>

      <section className="border-t border-line/50">
        <div className="wrap grid gap-12 py-24 lg:grid-cols-12 lg:py-32">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="kicker">The perspective behind Operating Reality</p>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <div className="max-w-2xl space-y-5 text-base leading-relaxed text-slate-400">
                <p className="text-xl font-medium leading-snug tracking-tight text-slate-100 sm:text-2xl">
                  Operating Reality grew from a simple observation: technology is often asked to
                  solve problems that technology didn't create.
                </p>
                <p>
                  Processes evolve. Knowledge becomes concentrated in individuals. Information
                  gets scattered. Ownership becomes unclear. Organisations add tools to
                  compensate.
                </p>
                <p>
                  Eventually, the organisation becomes harder to understand than the technology
                  itself.
                </p>
                <p className="text-slate-200">
                  Operating Reality exists to look underneath that complexity.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-line/50 bg-charcoal/20">
        <div className="wrap py-24 lg:py-32">
          <Reveal>
            <p className="kicker">Professional profile</p>
            <h2 className="mt-6 text-3xl font-medium tracking-tight text-slate-100">Anna Krat</h2>
            <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
              Technical Product & Digital Transformation Professional
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-600">
              Areas of experience
            </p>
            <ul className="mt-5 flex max-w-3xl flex-wrap gap-2" data-testid="about-experience-areas">
              {EXPERIENCE_AREAS.map((a) => (
                <li
                  key={a}
                  className="border border-line/60 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-300 transition-colors duration-300 hover:border-glow/50 hover:text-glow"
                >
                  {a}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-12 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <a href={EXPERIENCE_URL} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="about-experience-cta">
                View professional experience <ArrowUpRight size={14} />
              </a>
              <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" className="btn-ghost" data-testid="about-linkedin-cta">
                LinkedIn <ArrowUpRight size={14} />
              </a>
              <Link to="/contact" className="btn-ghost" data-testid="about-contact-cta">
                Start a conversation <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
